import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  User
} from 'firebase/auth';
import { auth } from './firebase';
import { getFriendlyAuthErrorMessage } from '../utils/authErrorUtils';

export function subscribeAuthState(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export async function loginAdminWithEmail(email: string, pass: string): Promise<User> {
  try {
    const credential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), pass);
    return credential.user;
  } catch (error: any) {
    if (
      error.code === 'auth/user-not-found' ||
      error.code === 'auth/invalid-credential' ||
      error.code === 'auth/wrong-password' ||
      error.code === 'auth/invalid-login-credentials'
    ) {
      throw new Error('Incorrect email address or password.');
    }
    throw new Error(getFriendlyAuthErrorMessage(error));
  }
}

export async function registerAdminWithEmail(email: string, pass: string, displayName?: string): Promise<User> {
  try {
    const credential = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), pass);
    if (displayName && credential.user) {
      try {
        await updateProfile(credential.user, { displayName });
      } catch (e) {
        console.warn('Could not set displayName on Firebase Auth user', e);
      }
    }
    return credential.user;
  } catch (error: any) {
    throw new Error(getFriendlyAuthErrorMessage(error));
  }
}

export async function logoutAdmin(): Promise<void> {
  await firebaseSignOut(auth);
}

export function getCurrentAdminUser(): User | null {
  return auth.currentUser;
}

export async function sendPasswordResetEmail(email: string): Promise<void> {
  const cleanEmail = email.trim().toLowerCase();
  const resetUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/reset-password`
    : 'https://uniformloop.org.uk/reset-password';

  try {
    // Attempt dispatch with ActionCodeSettings pointing to the custom /reset-password route
    await firebaseSendPasswordResetEmail(auth, cleanEmail, {
      url: resetUrl,
      handleCodeInApp: true,
    });
  } catch (err: any) {
    // If the domain is not yet whitelisted for continueUrl in Firebase Console, fallback to standard send
    if (err.code === 'auth/unauthorized-continue-uri' || err.message?.includes('unauthorized-continue-uri')) {
      console.warn('[Password Reset] Custom action URL domain not yet authorized in Firebase Console, using default action handler:', err);
      try {
        await firebaseSendPasswordResetEmail(auth, cleanEmail);
        return;
      } catch (fallbackErr: any) {
        throw new Error(getFriendlyAuthErrorMessage(fallbackErr));
      }
    }
    throw new Error(getFriendlyAuthErrorMessage(err));
  }
}
